import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createBreadcrumbStructuredData, createFAQStructuredData } from '../../utils/structuredData';

export default function Disadvantages() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sho-navi.com';

  // SEO設定
  usePageSEO({
    title: 'DMM FXのデメリット5選｜初心者には問題ない理由も解説',
    description: 'DMM FXのデメリットを正直に解説。最低取引単位10,000通貨、通貨ペア21種類、スキャルピンググレーゾーンなど。しかし初心者には影響が少ない理由と、メリットが上回る点を詳しく説明します。',
    keywords: 'DMM FX, デメリット, 欠点, 短所, 最低取引単位, 通貨ペア, スキャルピング',
    ogImage: 'https://public.readdy.ai/ai/img_res/a2553cd4cb817e259f93f04ec19dce9e.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXのデメリット5選',
        description: 'DMM FXのデメリットを包み隠さず解説。最低取引単位、通貨ペア数、スキャルピング制限などの欠点と、初心者には問題にならない理由を詳しく説明します。',
        image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20disadvantages&width=1200&height=630&seq=disadvantages-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: `${siteUrl}/disadvantages`
      }),
      // FAQ構造化データ
      createFAQStructuredData([
        {
          question: 'DMM FXの最低取引単位が10,000通貨なのはデメリットですか?',
          answer: '他社では1,000通貨や1通貨から取引できる業者もありますが、DMM FXはデモ口座で無料練習が可能です。また、レバレッジ25倍を活用すれば約6万円から取引できるため、初心者にとって大きな問題にはなりません。'
        },
        {
          question: 'DMM FXの通貨ペアが21種類しかないのは少ないですか?',
          answer: '米ドル/円、ユーロ/円、ポンド/円など主要通貨ペアは全て揃っています。初心者はまず主要通貨で取引すべきであり、マイナー通貨は流動性が低くリスクが高いため、21種類で十分です。'
        },
        {
          question: 'DMM FXではスキャルピングができないのですか?',
          answer: 'DMM FXは秒単位の超高頻度取引は制限していますが、数分単位の取引（分スキャ）は概ね許容されています。初心者にはスキャルピングよりもデイトレードやスイングトレードが推奨されるため、大きな問題にはなりません。'
        },
        {
          question: 'DMM FXで自動売買（MT4/MT5）が使えないのはデメリットですか?',
          answer: '初心者はまず裁量取引で経験を積むことが重要です。自動売買は設定を誤ると大きな損失につながる可能性があります。DMM FXの独自ツールは初心者にも使いやすく、13種類のテクニカル指標で十分な分析が可能です。'
        },
        {
          question: 'DMM FXのデモ口座が3ヶ月で期限切れになるのは短いですか?',
          answer: '基本操作やチャートの見方は3ヶ月あれば十分習得できます。また、期限が切れても再度デモ口座を開設できるため、何度でも練習可能です。期限があることで集中して練習に取り組めるというメリットもあります。'
        }
      ], `${siteUrl}/disadvantages`),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'デメリット', url: '/disadvantages' }
      ], siteUrl)
    ]
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX基礎知識', url: '/fx-basic' },
            { name: 'デメリット', url: '/disadvantages' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              DMM FXのデメリット5選
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              良い点だけでなく、デメリットも正直にお伝えします。<br />
              ただし、<strong className="text-teal-600">初心者にとっては大きな問題にならない</strong>理由も詳しく解説します。
            </p>
          </div>

          {/* 結論を先に表示 */}
          <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-8 max-w-4xl mx-auto text-white shadow-xl">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-check-double-line text-4xl"></i>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center mb-4">
              【結論】<span className="bg-red-500 text-white px-3 py-1 rounded text-2xl">初心者には十分おすすめ</span>できます
            </h2>
            <p className="text-center text-white/90 leading-relaxed">
              デメリットはありますが、いずれも<strong>初心者には影響が少ない</strong>ものばかり。<br />
              むしろ<strong>業界最狭水準のスプレッド・充実のサポート・高い安全性</strong>など、<br />
              初心者が重視すべきポイントでは圧倒的に優れています。
            </p>
          </div>
        </div>
      </section>

      {/* 目次 */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <i className="ri-list-check text-teal-600 mr-2"></i>
            この記事で解説するデメリット
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            <a href="#demerit1" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <span className="w-6 h-6 bg-gray-100 rounded text-sm flex items-center justify-center mr-2 font-bold">1</span>
              最低取引単位が10,000通貨
            </a>
            <a href="#demerit2" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <span className="w-6 h-6 bg-gray-100 rounded text-sm flex items-center justify-center mr-2 font-bold">2</span>
              通貨ペアが21種類
            </a>
            <a href="#demerit3" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <span className="w-6 h-6 bg-gray-100 rounded text-sm flex items-center justify-center mr-2 font-bold">3</span>
              スキャルピングがグレーゾーン
            </a>
            <a href="#demerit4" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <span className="w-6 h-6 bg-gray-100 rounded text-sm flex items-center justify-center mr-2 font-bold">4</span>
              自動売買（MT4/MT5）非対応
            </a>
            <a href="#demerit5" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <span className="w-6 h-6 bg-gray-100 rounded text-sm flex items-center justify-center mr-2 font-bold">5</span>
              デモ口座の有効期限が3ヶ月
            </a>
          </div>
        </div>
      </section>

      {/* デメリット一覧 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="space-y-16">
            {/* デメリット1 */}
            <div id="demerit1" className="scroll-mt-24">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
                  <span className="text-2xl font-bold text-red-600">1</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">最低取引単位が10,000通貨</h3>
              </div>
              
              {/* なぜデメリットと言われるのか */}
              <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-red-500 text-xl mr-2"></i>
                  なぜデメリットと言われるのか？
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  SBI FXトレードや松井証券など、<strong>1通貨や1,000通貨から取引できる業者</strong>が増えている中、DMM FXは10,000通貨からの取引となります。米ドル/円の場合、最低でも<strong>約6万円の証拠金</strong>が必要になるため、「少額で練習したい」という初心者には敷居が高く感じられます。
                </p>
              </div>

              {/* 初心者には問題にならない理由 */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-200 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-shield-check-line text-teal-600 text-xl mr-2"></i>
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded text-lg">初心者には大きな問題にならない理由</span>
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">デモ口座で無料練習</span>
                    </div>
                    <p className="text-sm text-gray-600">本番と同じ環境で、リスクゼロで練習できます。操作に慣れてから本番取引を始められます。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">約6万円から取引可能</span>
                    </div>
                    <p className="text-sm text-gray-600">レバレッジ25倍を活用すれば、米ドル/円で約6万円から取引できます。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">真剣に取り組める</span>
                    </div>
                    <p className="text-sm text-gray-600">ある程度の資金で取引することで、緊張感を持って真剣にFXを学べます。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">キャッシュバックで実質負担減</span>
                    </div>
                    <p className="text-sm text-gray-600">最大50万円のキャッシュバックキャンペーンで、取引コストを回収できます。</p>
                  </div>
                </div>
              </div>

              {/* 他社比較 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-bar-chart-horizontal-line text-gray-600 text-xl mr-2"></i>
                  他社と比較すると？
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3 px-4 font-bold text-gray-900">FX業者</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">最低取引単位</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">必要証拠金目安</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 bg-teal-50">
                        <td className="py-3 px-4 font-bold text-teal-700">DMM FX</td>
                        <td className="py-3 px-4 text-center">10,000通貨</td>
                        <td className="py-3 px-4 text-center">約6万円</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">GMOクリック証券</td>
                        <td className="py-3 px-4 text-center">1,000通貨</td>
                        <td className="py-3 px-4 text-center">約6,000円</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">SBI FXトレード</td>
                        <td className="py-3 px-4 text-center">1通貨</td>
                        <td className="py-3 px-4 text-center">約6円</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">松井証券</td>
                        <td className="py-3 px-4 text-center">1通貨</td>
                        <td className="py-3 px-4 text-center">約6円</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 mt-4">
                  ※米ドル/円=150円、レバレッジ25倍で計算
                </p>
              </div>
            </div>

            {/* デメリット2 */}
            <div id="demerit2" className="scroll-mt-24">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
                  <span className="text-2xl font-bold text-red-600">2</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">通貨ペアが21種類</h3>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-red-500 text-xl mr-2"></i>
                  なぜデメリットと言われるのか？
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  IG証券（約100種類）やサクソバンク証券（約150種類）など、<strong>多数の通貨ペアを扱う業者</strong>と比べると選択肢が限られます。特に、マイナー通貨やエキゾチック通貨ペアで取引したい方には物足りなく感じられます。
                </p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-200 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-shield-check-line text-teal-600 text-xl mr-2"></i>
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded text-lg">初心者には大きな問題にならない理由</span>
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">主要通貨ペアは完備</span>
                    </div>
                    <p className="text-sm text-gray-600">米ドル/円、ユーロ/円、ポンド/円など、初心者が取引すべき主要通貨ペアはすべて揃っています。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">マイナー通貨はリスク大</span>
                    </div>
                    <p className="text-sm text-gray-600">マイナー通貨は流動性が低く、スプレッドも広いため、初心者には不向きです。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">高金利通貨も対応</span>
                    </div>
                    <p className="text-sm text-gray-600">メキシコペソ/円、南アフリカランド/円、トルコリラ/円など、スワップ狙いの通貨も取引可能。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">情報収集しやすい</span>
                    </div>
                    <p className="text-sm text-gray-600">主要通貨は情報が豊富で、分析しやすいため初心者の学習に最適です。</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-bar-chart-horizontal-line text-gray-600 text-xl mr-2"></i>
                  他社と比較すると？
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3 px-4 font-bold text-gray-900">FX業者</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">通貨ペア数</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">特徴</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 bg-teal-50">
                        <td className="py-3 px-4 font-bold text-teal-700">DMM FX</td>
                        <td className="py-3 px-4 text-center">21種類</td>
                        <td className="py-3 px-4 text-center">主要通貨に厳選</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">GMOクリック証券</td>
                        <td className="py-3 px-4 text-center">20種類</td>
                        <td className="py-3 px-4 text-center">主要通貨中心</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">ヒロセ通商</td>
                        <td className="py-3 px-4 text-center">54種類</td>
                        <td className="py-3 px-4 text-center">マイナー通貨も豊富</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">IG証券</td>
                        <td className="py-3 px-4 text-center">約100種類</td>
                        <td className="py-3 px-4 text-center">エキゾチック通貨多数</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 mt-4">
                  ※GMOクリック証券と同程度。初心者には十分な品揃えです。
                </p>
              </div>
            </div>

            {/* デメリット3 */}
            <div id="demerit3" className="scroll-mt-24">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
                  <span className="text-2xl font-bold text-red-600">3</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">スキャルピングがグレーゾーン</h3>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-red-500 text-xl mr-2"></i>
                  なぜデメリットと言われるのか？
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  DMM FXはスキャルピング（超短期売買）を<strong>明確に禁止していませんが、公認もしていません</strong>。取引約款では「短時間での連続した取引」が制限対象として記載されており、<strong>秒単位の超高頻度取引</strong>は口座凍結のリスクがあります。
                </p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-200 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-shield-check-line text-teal-600 text-xl mr-2"></i>
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded text-lg">初心者には大きな問題にならない理由</span>
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">初心者にスキャルピングは不向き</span>
                    </div>
                    <p className="text-sm text-gray-600">スキャルピングは高度な技術と経験が必要。初心者はまずデイトレードやスイングトレードから始めるべきです。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">通常の取引は問題なし</span>
                    </div>
                    <p className="text-sm text-gray-600">数分〜数時間単位の取引であれば、まったく問題ありません。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">分スキャは概ね許容</span>
                    </div>
                    <p className="text-sm text-gray-600">手動での数分単位の取引（分スキャ）であれば、概ね許容される傾向にあります。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">狭いスプレッドで有利</span>
                    </div>
                    <p className="text-sm text-gray-600">業界最狭水準のスプレッドなので、通常の短期売買でも十分利益を狙えます。</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-bar-chart-horizontal-line text-gray-600 text-xl mr-2"></i>
                  他社と比較すると？
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3 px-4 font-bold text-gray-900">FX業者</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">スキャルピング</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">備考</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 bg-teal-50">
                        <td className="py-3 px-4 font-bold text-teal-700">DMM FX</td>
                        <td className="py-3 px-4 text-center">グレーゾーン</td>
                        <td className="py-3 px-4 text-center">分スキャは概ね許容</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">ヒロセ通商</td>
                        <td className="py-3 px-4 text-center text-teal-600 font-bold">公認</td>
                        <td className="py-3 px-4 text-center">公式に推奨</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">JFX</td>
                        <td className="py-3 px-4 text-center text-teal-600 font-bold">公認</td>
                        <td className="py-3 px-4 text-center">社長が公認</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">GMOクリック証券</td>
                        <td className="py-3 px-4 text-center">グレーゾーン</td>
                        <td className="py-3 px-4 text-center">手動なら概ね許容</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 mt-4">
                  ※スキャルピングを本格的に行いたい場合は、ヒロセ通商やJFXがおすすめです。
                </p>
              </div>

              {/* 利用者の声へのリンク */}
              <div className="mt-6 bg-white rounded-xl p-6 border-l-4 border-teal-500 shadow-md">
                <p className="text-gray-700 leading-relaxed">
                  実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
                  → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
                </p>
              </div>
            </div>

            {/* デメリット4 */}
            <div id="demerit4" className="scroll-mt-24">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
                  <span className="text-2xl font-bold text-red-600">4</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">自動売買（MT4/MT5）非対応</h3>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-red-500 text-xl mr-2"></i>
                  なぜデメリットと言われるのか？
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  DMM FXでは<strong>MT4/MT5などの自動売買ツール（EA）が使えません</strong>。システムトレードで24時間自動的に取引したい方や、独自のアルゴリズムで取引したい上級者には不向きです。
                </p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-200 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-shield-check-line text-teal-600 text-xl mr-2"></i>
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded text-lg">初心者には大きな問題にならない理由</span>
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">初心者は裁量取引から</span>
                    </div>
                    <p className="text-sm text-gray-600">まずは自分で判断して取引する「裁量取引」で経験を積むことが大切です。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">自動売買はリスクも大きい</span>
                    </div>
                    <p className="text-sm text-gray-600">自動売買は設定を誤ると大きな損失につながる可能性があります。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">高機能チャートで分析可能</span>
                    </div>
                    <p className="text-sm text-gray-600">DMM FXの取引ツールは13種類のテクニカル指標を搭載。十分な分析が可能です。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">使いやすさを優先</span>
                    </div>
                    <p className="text-sm text-gray-600">独自ツールだからこそ、初心者にも使いやすいシンプルな設計になっています。</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-bar-chart-horizontal-line text-gray-600 text-xl mr-2"></i>
                  他社と比較すると？
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3 px-4 font-bold text-gray-900">FX業者</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">MT4/MT5</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">独自ツール</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 bg-teal-50">
                        <td className="py-3 px-4 font-bold text-teal-700">DMM FX</td>
                        <td className="py-3 px-4 text-center text-red-500">非対応</td>
                        <td className="py-3 px-4 text-center text-teal-600 font-bold">高機能</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">GMOクリック証券</td>
                        <td className="py-3 px-4 text-center text-red-500">非対応</td>
                        <td className="py-3 px-4 text-center text-teal-600 font-bold">高機能</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">OANDA証券</td>
                        <td className="py-3 px-4 text-center text-teal-600 font-bold">対応</td>
                        <td className="py-3 px-4 text-center">あり</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">楽天証券</td>
                        <td className="py-3 px-4 text-center text-teal-600 font-bold">対応</td>
                        <td className="py-3 px-4 text-center">あり</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 mt-4">
                  ※GMOクリック証券も同様にMT4非対応。大手FX業者では珍しくありません。
                </p>
              </div>
            </div>

            {/* デメリット5 */}
            <div id="demerit5" className="scroll-mt-24">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
                  <span className="text-2xl font-bold text-red-600">5</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">デモ口座の有効期限が3ヶ月</h3>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-red-500 text-xl mr-2"></i>
                  なぜデメリットと言われるのか？
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  DMM FXのデモ口座は<strong>開設から3ヶ月で自動的に削除</strong>されます。無期限でデモ取引できる業者と比べると、長期間じっくり練習したい方には短く感じられます。
                </p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-200 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-shield-check-line text-teal-600 text-xl mr-2"></i>
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded text-lg">初心者には大きな問題にならない理由</span>
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">3ヶ月で十分習得可能</span>
                    </div>
                    <p className="text-sm text-gray-600">基本操作やチャートの見方は、3ヶ月あれば十分に習得できます。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">再開設が可能</span>
                    </div>
                    <p className="text-sm text-gray-600">期限が切れても、再度デモ口座を開設できます。何度でも練習可能です。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">本番で学ぶことも大切</span>
                    </div>
                    <p className="text-sm text-gray-600">デモと本番では心理状態が異なります。ある程度慣れたら本番で経験を積むことも重要です。</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center mb-2">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="font-bold text-gray-900">期限があるから集中できる</span>
                    </div>
                    <p className="text-sm text-gray-600">期限があることで、ダラダラせずに集中して練習に取り組めます。</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-bar-chart-horizontal-line text-gray-600 text-xl mr-2"></i>
                  他社と比較すると？
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3 px-4 font-bold text-gray-900">FX業者</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">デモ口座期限</th>
                        <th className="text-center py-3 px-4 font-bold text-gray-900">再開設</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 bg-teal-50">
                        <td className="py-3 px-4 font-bold text-teal-700">DMM FX</td>
                        <td className="py-3 px-4 text-center">3ヶ月</td>
                        <td className="py-3 px-4 text-center text-teal-600 font-bold">可能</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">GMOクリック証券</td>
                        <td className="py-3 px-4 text-center">1ヶ月</td>
                        <td className="py-3 px-4 text-center">可能</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">外為どっとコム</td>
                        <td className="py-3 px-4 text-center">90日</td>
                        <td className="py-3 px-4 text-center">可能</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4">ヒロセ通商</td>
                        <td className="py-3 px-4 text-center text-teal-600 font-bold">無期限</td>
                        <td className="py-3 px-4 text-center">-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 mt-4">
                  ※GMOクリック証券（1ヶ月）より長い。業界では標準的な期間です。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 初心者に問題ない理由まとめ */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-teal-100 rounded-full mb-6">
              <i className="ri-check-double-line text-4xl text-teal-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">初心者には問題ない5つの理由</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              デメリットを詳しく見てきましたが、<strong className="text-teal-600">初心者にとっては大きな問題にならない</strong>ことがお分かりいただけたと思います。
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-4 mb-12">
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-teal-600">1</span>
              </div>
              <p className="text-sm font-bold text-gray-900">デモ口座で<br />無料練習可能</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-teal-600">2</span>
              </div>
              <p className="text-sm font-bold text-gray-900">主要通貨で<br />十分取引可能</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-teal-600">3</span>
              </div>
              <p className="text-sm font-bold text-gray-900">通常取引は<br />問題なし</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-teal-600">4</span>
              </div>
              <p className="text-sm font-bold text-gray-900">裁量取引から<br />始めるべき</p>
            </div>
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-teal-600">5</span>
              </div>
              <p className="text-sm font-bold text-gray-900">3ヶ月で<br />十分習得可能</p>
            </div>
          </div>

          {/* メリットの強調 */}
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              むしろ初心者が重視すべきポイントでは<span className="bg-red-100 text-red-700 px-3 py-1 rounded text-2xl ml-2">圧倒的に優れています</span>
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">
                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded">業界最狭水準のスプレッド</span>
                </h4>
                <p className="text-sm text-gray-600">米ドル/円0.2銭で取引コストを最小限に抑えられます</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">
                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded">24時間サポート体制</span>
                </h4>
                <p className="text-sm text-gray-600">電話・メール・LINEで困ったときにすぐ相談できます</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">
                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded">全額信託保全で安心</span>
                </h4>
                <p className="text-sm text-gray-600">金融庁登録・全額信託保全で資産が守られます</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* デメリットvsメリット比較 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">デメリットとメリットを比較</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-close-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">デメリット5つ</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-red-500 mr-2 mt-1"></i>
                  <div>
                    <span className="text-gray-700">最低取引単位が10,000通貨</span>
                    <span className="block text-xs text-gray-500 mt-1">→ デモ口座で練習可能</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-red-500 mr-2 mt-1"></i>
                  <div>
                    <span className="text-gray-700">通貨ペアが21種類</span>
                    <span className="block text-xs text-gray-500 mt-1">→ 主要通貨は完備</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-red-500 mr-2 mt-1"></i>
                  <div>
                    <span className="text-gray-700">スキャルピングがグレーゾーン</span>
                    <span className="block text-xs text-gray-500 mt-1">→ 初心者には不要</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-red-500 mr-2 mt-1"></i>
                  <div>
                    <span className="text-gray-700">自動売買（MT4/MT5）非対応</span>
                    <span className="block text-xs text-gray-500 mt-1">→ 裁量取引から始めるべき</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-red-500 mr-2 mt-1"></i>
                  <div>
                    <span className="text-gray-700">デモ口座の有効期限が3ヶ月</span>
                    <span className="block text-xs text-gray-500 mt-1">→ 再開設可能</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-8 border-2 border-teal-200">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-check-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">メリット多数</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-star-fill text-amber-500 mr-2 mt-1"></i>
                  <div>
                    <span className="font-bold text-gray-900">
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded">業界最狭水準のスプレッド</span>
                    </span>
                    <span className="block text-xs text-teal-600 mt-1">米ドル/円0.2銭</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-star-fill text-amber-500 mr-2 mt-1"></i>
                  <div>
                    <span className="font-bold text-gray-900">
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded">24時間サポート体制</span>
                    </span>
                    <span className="block text-xs text-teal-600 mt-1">電話・メール・LINE対応</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-star-fill text-amber-500 mr-2 mt-1"></i>
                  <div>
                    <span className="font-bold text-gray-900">
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded">全額信託保全で安心</span>
                    </span>
                    <span className="block text-xs text-teal-600 mt-1">金融庁登録業者</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-star-fill text-amber-500 mr-2 mt-1"></i>
                  <div>
                    <span className="font-bold text-gray-900">
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded">高機能スマホアプリ</span>
                    </span>
                    <span className="block text-xs text-teal-600 mt-1">外出先でも快適取引</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-star-fill text-amber-500 mr-2 mt-1"></i>
                  <div>
                    <span className="font-bold text-gray-900">
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded">最大50万円キャッシュバック</span>
                    </span>
                    <span className="block text-xs text-teal-600 mt-1">お得に始められる</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 最終結論 */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-teal-500 rounded-full mb-8">
            <i className="ri-thumb-up-fill text-5xl text-white"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            結論：<span className="bg-red-500 text-white px-4 py-2 rounded text-3xl">初心者には十分おすすめ</span>できます
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
            デメリットはありますが、いずれも<strong className="text-teal-400">初心者には影響が少ない</strong>ものばかりです。<br />
            むしろ、<strong className="text-teal-400">業界最狭水準のスプレッド・充実のサポート・高い安全性</strong>など、<br />
            初心者が重視すべきポイントでは圧倒的に優れています。
          </p>
          
          <div className="bg-white/10 backdrop-blur rounded-xl p-6 mb-8 max-w-2xl mx-auto">
            <p className="text-white font-bold mb-4">こんな方にDMM FXはおすすめ</p>
            <div className="flex flex-wrap justify-center gap-3">
              <span className="bg-teal-500/30 text-teal-300 px-4 py-2 rounded-full text-sm">FX初心者</span>
              <span className="bg-teal-500/30 text-teal-300 px-4 py-2 rounded-full text-sm">取引コストを抑えたい方</span>
              <span className="bg-teal-500/30 text-teal-300 px-4 py-2 rounded-full text-sm">サポート重視の方</span>
              <span className="bg-teal-500/30 text-teal-300 px-4 py-2 rounded-full text-sm">安全性重視の方</span>
              <span className="bg-teal-500/30 text-teal-300 px-4 py-2 rounded-full text-sm">スマホで取引したい方</span>
            </div>
          </div>

          {/* 口座開設手順へのリンク */}
          <div className="mb-8 bg-white/10 backdrop-blur rounded-xl p-6 max-w-2xl mx-auto">
            <p className="text-white leading-relaxed">
              口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
              → <Link to="/account-opening" className="text-teal-300 font-semibold hover:text-teal-200 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
            </p>
          </div>

          <a 
            href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
            rel="nofollow"
            className="inline-block bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-12 py-5 rounded-xl text-lg font-bold hover:from-teal-600 hover:to-emerald-600 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
          >
            無料で口座開設する
          </a>
          <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          <p className="text-sm text-gray-400 mt-6">
            口座開設は最短5分 | 口座維持費無料 | 最大50万円キャッシュバック
          </p>
        </div>
      </section>

      {/* 関連記事 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">関連記事</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/comparison" className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-200 transition-colors">
                <i className="ri-scales-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">他社との徹底比較</h3>
              <p className="text-sm text-gray-600">GMOクリック証券との違いを詳しく解説</p>
            </Link>
            <Link to="/spread" className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-colors">
                <i className="ri-funds-line text-2xl text-emerald-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">スプレッドは本当に狭い？</h3>
              <p className="text-sm text-gray-600">取引コストを他社と徹底比較</p>
            </Link>
            <Link to="/how-to-choose" className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-200 transition-colors">
                <i className="ri-search-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">FX会社の選び方</h3>
              <p className="text-sm text-gray-600">自分に合ったFX会社を見つける方法</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}
